import React, { useState } from 'react';
import { styled } from '@mui/material/styles';
import { Box, CircularProgress } from '@mui/material';
import { useNavigate } from 'react-router-dom';

import { useAppDispatch } from '../../hooks/useAppDispatch';
import { useAppSelector } from '../../hooks/useAppSelector';
import * as userSlice from '../../data/user.slice';
import * as referenceSlice from '../../data/reference.slice';
import * as globalSettingsSlice from '../../data/settings.slice';
import * as providerSlice from '../../data/provider.slice';
import HomeAppBar from '../../components/home/HomeAppBar';
import HomeRouting from '../../routing/HomeRouting';
import OnboardRouting from '../../routing/OnboardRouting';
import HomeFooterBar from '../../components/home/HomeFooterBar';
import EulaDialog from '../../components/home/EulaDialog';
import { useIsOnboardingUser } from '../../hooks/useIsOnboardingUser';
import authService from '../../services/authService';
import HomeNavBar from '../../components/home/HomeNavBar';
import { webSocketService } from '../../services/webSocketService';
import ESADialog from '../../components/home/ESADialog';
import { HomeSnackBar } from '../../components/home/HomeSnackBar';
import { useLoggedInProviderId } from '../../hooks/useLoggedInProviderId';
import { useIsTransportationProviderUser } from '../../hooks/useIsTransportationProviderUser';
import { setFullStoryUserProperties } from '../../services/fullStoryService';

const PREFIX = 'Home';

const classes = {
  container: `${PREFIX}-container`,
  content: `${PREFIX}-content`,
  scroller: `${PREFIX}-scroller`,
  progress: `${PREFIX}-progress`,
  progressWrapper: `${PREFIX}-progressWrapper`,
};

const Root = styled('div')(({ theme }) => ({
  [`&.${classes.container}`]: {
    display: 'flex',
    flex: '0 1 100%',
    flexDirection: 'column',
  },

  [`&.${classes.progressWrapper}`]: {
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    backgroundColor: '#ffffff',
    width: '100%',
  },

  [`& .${classes.content}`]: {
    display: 'flex',
    flexGrow: 1,
    paddingBottom: 0,
    backgroundColor: theme.palette.background.paper,
    backgroundImage: 'url(/bggray.png)',
    backgroundRepeatX: 'repeat',
    backgroundRepeatY: 'no-repeat',
  },

  [`& .${classes.scroller}`]: {
    flex: 1,
    paddingBottom: 20,
    backgroundColor: 'white',
    maxWidth: 'calc(100vw - 248px)',
  },

  [`& .${classes.progress}`]: {
    marginTop: theme.spacing(4),
    marginLeft: 'auto',
    marginRight: 'auto',
  },
}));

export default function Home() {
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const userInfo = useAppSelector(userSlice.selectUserInfo);
  const isOnboarding = useIsOnboardingUser();
  const providerID = useLoggedInProviderId();
  const isTransportationProviderUser = useIsTransportationProviderUser();

  const [loggingOut, setLoggingOut] = useState(false);
  const [eulaOpen, setEulaOpen] = useState(false);
  const [esaOpen, setEsaOpen] = useState(false);

  React.useEffect(() => {
    if (
      userInfo &&
      !userInfo.agreedToEULA &&
      isTransportationProviderUser &&
      !userInfo.onboardingUser
    ) {
      const appTitleID = userInfo.applicationTitleIDs?.[0];
      dispatch(userSlice.getEULAInfoAsync(appTitleID));
      setEulaOpen(true);
    }

    if (
      userInfo &&
      userInfo.agreedToEULA &&
      !userInfo.agreedToExternalSupportAgreement &&
      isTransportationProviderUser
    ) {
      const appTitleID = userInfo.applicationTitleIDs?.[0];
      if (appTitleID) {
        dispatch(userSlice.getExternalSupportAgreementInfoAsync(appTitleID));
        setEsaOpen(true);
      }
    }

    if (userInfo?.userAccountID) {
      webSocketService.setSessionInfo({
        userAccountID: userInfo.userAccountID,
      });
    }
  }, [userInfo, isOnboarding, dispatch, isTransportationProviderUser]);

  React.useEffect(() => {
    if (providerID) {
      webSocketService.setSessionInfo({
        loggedInTransportationProviderID: providerID,
      });
    }
  }, [providerID]);

  React.useEffect(() => {
    dispatch(userSlice.fetchUserInfoAsync());
    dispatch(referenceSlice.fetchTransportationTypesAsync());
    dispatch(referenceSlice.fetchLegalEntityStatusesAsync());
    dispatch(referenceSlice.fetchLegalEntityTypesAsync());
    dispatch(referenceSlice.fetchStateCodesAsync());
    dispatch(referenceSlice.fetchInsuranceStrengthsAsync());
    dispatch(referenceSlice.fetchMessagingMethodsAsync());
    dispatch(referenceSlice.fetchVehicleAttributeTypesAsync());
    dispatch(referenceSlice.fetchVehicleColorsAsync());
    dispatch(referenceSlice.fetchProviderDocumentTypesAsync());
    dispatch(referenceSlice.fetchDriverDocumentTypesAsync());
    dispatch(referenceSlice.fetchVehicleDocumentTypesAsync());
    dispatch(referenceSlice.fetchVehicleTypesAsync());
    dispatch(referenceSlice.fetchUserTypesAsync());
    dispatch(referenceSlice.fetchApplicationTitlesAsync());
    dispatch(referenceSlice.fetchApplicationSettingsAsync());
    dispatch(globalSettingsSlice.fetchAllApplicationGlobalSettingsAsync());
    dispatch(referenceSlice.fetchTimeZonesAsync());
    dispatch(referenceSlice.fetchFrequencyDayOfWeeksAsync());
    dispatch(referenceSlice.fetchTripModesAsync());
    dispatch(referenceSlice.fetchTripLegPackageRejectReasonsAsync());
    dispatch(providerSlice.fetchCoverageAreasAsync());

    webSocketService.connect(dispatch);
    authService.getTokens().subscribe((tokens) => {
      webSocketService.setSessionInfo({
        accessToken: tokens.accessToken,
        accessTokenID: tokens.idToken,
      });
    });
    authService.setDispatch(dispatch);
    return () => {
      webSocketService.disconnect();
    };
  }, [dispatch]);

  React.useEffect(() => {
    if (userInfo?.emailAddress) {
      void setFullStoryUserProperties(userInfo.emailAddress);
    }
  }, [userInfo?.emailAddress]);

  const handleEulaClose = (version: number | null) => {
    if (version) {
      dispatch(
        userSlice.updateEULAAgreementAsync(userInfo.userAccountID, version),
      );
    } else {
      setLoggingOut(true);
      authService.logout().then(() => navigate('/login'));
    }
    setEulaOpen(false);
  };

  const handleESAClose = (version: number | null) => {
    if (version) {
      dispatch(
        userSlice.updateExternalSupportAgreementAsync(
          userInfo.userAccountID,
          version,
        ),
      );
    } else {
      setLoggingOut(true);
      authService.logout().then(() => navigate('/login'));
    }
    setEsaOpen(false);
  };

  if (!userInfo) {
    return (
      <Root className={classes.progressWrapper}>
        <CircularProgress className={classes.progress} />
      </Root>
    );
  }

  if (userInfo && userInfo.userAccountState !== 'active') {
    navigate('/deactivated');
  }

  return (
    <Root className={classes.container}>
      {loggingOut ? (
        <CircularProgress className={classes.progress} />
      ) : (
        <>
          <HomeAppBar />
          <HomeSnackBar />
          <Box className={classes.content}>
            {isOnboarding ? (
              <OnboardRouting />
            ) : (
              <>
                <HomeNavBar />
                <Box className={classes.scroller}>
                  <HomeRouting />
                </Box>
              </>
            )}
          </Box>
          <HomeFooterBar />
        </>
      )}
      <EulaDialog open={eulaOpen} close={handleEulaClose} />
      <ESADialog open={esaOpen} close={handleESAClose} />
    </Root>
  );
}
